import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import Toast from '../../components/forms/Toast';
import TimeDeltaInputField from '../../components/forms/TimeDeltaInputField';
import { fetchUserInfo, teamPhoto } from '../../utils';
import { play } from '../../hooks/useSound';

/* -------------------------------------------------------------- *
 *  Mobile-first crew scoring screen.                              *
 *                                                                 *
 *  Form submission contract is preserved 1:1 with the previous    *
 *  version — same field names, same per-team field order, same    *
 *  FormData -> JSON shape — so the backend logic in               *
 *  api/routes/event.py:post_crew_score keeps working.             *
 * -------------------------------------------------------------- */

const SECONDARY = 'var(--bs-secondary, #6c757d)';
const SECONDARY_RGB = 'var(--bs-secondary-rgb, 108,117,125)';

const styles = {
  fieldLabel: {
    color: '#6b7280',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    display: 'block',
    textAlign: 'left',
  },
  bigInput: {
    width: '100%',
    height: '48px',
    border: '1px solid #d1d5db',
    borderRadius: '10px',
    background: '#fff',
    textAlign: 'center',
    fontSize: '1.15rem',
    fontWeight: 600,
    color: '#1f2937',
    fontFamily: 'Poppins',
    outline: 'none',
    boxSizing: 'border-box',
  },
  stepBtn: {
    width: '44px',
    height: '48px',
    flexShrink: 0,
    borderRadius: '10px',
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontSize: '1.4rem',
    fontWeight: 700,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    touchAction: 'manipulation',
  },
  switchTrack: {
    width: '2.75rem',
    height: '1.6rem',
    marginLeft: 0,
    cursor: 'pointer',
  },
  saveBarWrapper: {
    position: 'fixed',
    bottom: '60px', // sit above the bottom NavBar
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 'var(--app-max-width, 600px)',
    padding: '0.6rem 0.9rem 0.9rem',
    background: 'linear-gradient(to top, rgba(229,231,235,1) 65%, rgba(229,231,235,0))',
    zIndex: 100,
    pointerEvents: 'none',
  },
  saveBtn: {
    width: '100%',
    padding: '0.95rem 1rem',
    fontSize: '1.05rem',
    fontWeight: 700,
    borderRadius: '14px',
    border: 'none',
    background: SECONDARY,
    color: '#fff',
    boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
    pointerEvents: 'auto',
    cursor: 'pointer',
    fontFamily: 'Poppins',
  },
};

/* -------------------------- Stepper ---------------------------- */
const Stepper = ({ name, defaultValue, min = 0, max, placeholder = '0', disabled }) => {
  const init = defaultValue === undefined || defaultValue === null ? '' : String(defaultValue);
  const [val, setVal] = useState(init);

  const clamp = (n) => {
    let x = n;
    if (min !== undefined && min !== null && x < min) x = min;
    if (max !== undefined && max !== null && x > max) x = max;
    return x;
  };

  const inc = (e) => {
    e.preventDefault();
    const n = Number(val) || 0;
    setVal(String(clamp(n + 1)));
  };
  const dec = (e) => {
    e.preventDefault();
    const n = Number(val) || 0;
    setVal(String(clamp(n - 1)));
  };

  return (
    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
      <button
        type="button"
        onClick={dec}
        disabled={disabled}
        aria-label="decrement"
        style={{ ...styles.stepBtn, opacity: disabled ? 0.5 : 1 }}
      >
        −
      </button>
      <input
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        name={name}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onFocus={(e) => e.target.select()}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        style={{ ...styles.bigInput, flex: 1, minWidth: 0 }}
      />
      <button
        type="button"
        onClick={inc}
        disabled={disabled}
        aria-label="increment"
        style={{ ...styles.stepBtn, opacity: disabled ? 0.5 : 1 }}
      >
        +
      </button>
    </div>
  );
};

/* ------------------------- SwitchRow --------------------------- */
const SwitchRow = ({ name, label, defaultChecked, icon, disabled }) => {
  const [checked, setChecked] = useState(!!defaultChecked);

  const trackChecked = {
    backgroundColor: SECONDARY,
    borderColor: SECONDARY,
    boxShadow: `0 0 0 .15rem rgba(${SECONDARY_RGB}, .15)`,
  };

  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        padding: '0.55rem 0',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        width: '100%',
        margin: 0,
        userSelect: 'none',
      }}
    >
      {icon && (
        <Icon
          name={icon}
          theme={checked ? 'secondary' : 'light'}
          style={{ fontSize: '1.05rem', color: checked ? SECONDARY : '#9ca3af' }}
        />
      )}
      <span style={{ flex: 1, color: '#374151', fontSize: '0.95rem', fontWeight: 500, textAlign: 'left' }}>
        {label}
      </span>
      <div className="form-check form-switch m-0 p-0" style={{ minHeight: 0 }}>
        <input
          type="checkbox"
          className="form-check-input"
          role="switch"
          checked={checked}
          onChange={(e) => !disabled && setChecked(e.target.checked)}
          disabled={disabled}
          style={{ ...styles.switchTrack, ...(checked ? trackChecked : {}) }}
        />
      </div>
      <input type="hidden" name={name} value={checked ? 'on' : 'off'} />
    </label>
  );
};

/* ----------------------- Team card ----------------------------- */
const TeamCard = ({ team, index, t }) => {
  const isJoker = team.joker_id && team.joker_id === team.activity_id;

  return (
    <Card>
      {/* hidden ids MUST come first per team so the backend can resolve
          score_id / challenge_score_id before any field referencing them */}
      <input type="hidden" name={`score_id.${index}`} value={team.score_id ?? ''} />
      <input type="hidden" name={`challenge_score_id.${index}`} value={team.challenge_score_id ?? ''} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <Image
          size="3rem"
          className="circle"
          to="/team"
          onClick={() => sessionStorage.setItem('otherTeamId', team.team_id)}
          url={teamPhoto(team.team_photo)}
        />
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2, color: '#111827' }}>
            {team.name}
          </div>
          <div style={{ color: '#6b7280', fontSize: '0.78rem', marginTop: '0.1rem' }}>
            <Icon name="corporate" style={{ fontSize: '11px' }} className="me-1" />
            {team.company_name}
          </div>
          <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
            <Icon name="user-crown" style={{ fontSize: '11px' }} className="me-1" />
            {team.captain_name}
          </div>
        </div>
        {isJoker && (
          <div
            style={{
              textAlign: 'center',
              color: '#b45309',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '8px',
              padding: '0.2rem 0.4rem',
              minWidth: '54px',
            }}
          >
            <Icon name="star" theme="primary" style={{ color: '#b45309', fontSize: '1rem' }} />
            <div style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.05em' }}>
              {t('joker')}
            </div>
          </div>
        )}
      </div>

      {/* Score / time fields (order doesn't affect backend) */}
      <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
        {team.score_1_label && (
          <div>
            <label style={styles.fieldLabel}>{team.score_1_label}</label>
            <Stepper name={`score_1.${index}`} defaultValue={team.score_1} placeholder="0" />
          </div>
        )}
        {team.score_2_label && (
          <div>
            <label style={styles.fieldLabel}>{team.score_2_label}</label>
            <Stepper name={`score_2.${index}`} defaultValue={team.score_2} placeholder="0" />
          </div>
        )}
        {team.time_1_label && (
          <div>
            <TimeDeltaInputField
              placeholder="MM:SS:SSS"
              label={team.time_1_label}
              className=""
              value={team.time_1}
              mask="99:99:999"
              type="text"
              name={`time_1.${index}`}
              mb="0"
              labelStyle={styles.fieldLabel}
            />
          </div>
        )}
        {team.time_2_label && (
          <div>
            <TimeDeltaInputField
              placeholder="MM:SS:SSS"
              label={team.time_2_label}
              className=""
              value={team.time_2}
              mask="99:99:999"
              type="text"
              name={`time_2.${index}`}
              mb="0"
              labelStyle={styles.fieldLabel}
            />
          </div>
        )}

        {/* Rank MUST come before is_ladies / is_joker — the backend
            uses the previously-seen rank value when processing those */}
        <div>
          <label style={styles.fieldLabel}>{t('rank')}</label>
          <Stepper name={`rank.${index}`} defaultValue={team.rank} min={1} placeholder="1" />
        </div>
      </div>

      {/* Toggles */}
      <div
        className="mt-3"
        style={{
          borderTop: '1px solid #f3f4f6',
          paddingTop: '0.25rem',
        }}
      >
        <SwitchRow
          name={`is_ladies.${index}`}
          label={t('ladies')}
          defaultChecked={team.is_ladies}
        />
        <SwitchRow
          name={`is_fairplay.${index}`}
          label={t('fairplay')}
          defaultChecked={team.is_fairplay}
        />
        <SwitchRow
          name={`is_disqualified.${index}`}
          label={t('disqualified')}
          icon="cross-circle"
          defaultChecked={team.is_disqualified}
        />
      </div>

      {/* joker stays hidden — read-only state from server */}
      <input
        type="hidden"
        name={`is_joker.${index}`}
        value={isJoker ? 'on' : 'off'}
      />
    </Card>
  );
};

/* --------------------------- Page ------------------------------ */
const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserInfo('/crew-score', setUserInfo, setLoading, setError, []);
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);

    const formData = new FormData(event.target);
    const values = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('/api/crew-score', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(values),
      });

      if (response.ok) {
        await response.json();
        setError(null);
        play('score-submit'); // plays through the SPA navigation below
        navigate('/crew-battles');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'somethingWentWrong');
      }
    } catch (err) {
      setError(err.message || 'somethingWentWrong');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const teams = userInfo?.teams || [];
  const first = teams[0];
  const canSave = !saving && !loading && teams.length > 0;

  return (
    <Base activeNav="home">
      <form onSubmit={onSubmit} style={{ paddingBottom: '140px' }}>
        {/* Activity header (sticky for context as user scrolls) */}
        {first && (
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 30,
              background: '#e5e7eb',
              paddingTop: '0.4rem',
            }}
          >
            <Card mb="2">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Image
                  url={first.activity_photo || require('./../../assets/images/fallback/activity.png')}
                  className="circle"
                  size="3.25rem"
                />
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div
                    style={{
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: '#111827',
                    }}
                  >
                    {first.activity_name}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: '0.1rem' }}>
                    <Icon name="clock" style={{ fontSize: '12px' }} className="me-1" />
                    {first.start_time} – {first.end_time}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                    {t('scoringType')}: {first.scoring_type}
                  </div>
                </div>
                <div
                  style={{
                    textAlign: 'center',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    padding: '0.25rem 0.55rem',
                    minWidth: '54px',
                  }}
                >
                  <div style={{ fontSize: '0.65rem', color: '#6b7280', letterSpacing: '0.04em' }}>
                    {t('teams')}
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#111827' }}>
                    {teams.length}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Empty state */}
        {!loading && teams.length === 0 && (
          <Card>
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '1.5rem 0' }}>
              {t('noTeams') || 'No teams to score yet.'}
            </div>
          </Card>
        )}

        {/* Team cards */}
        {teams.map((team, index) => (
          <TeamCard key={team.id ?? index} team={team} index={index} t={t} />
        ))}

        <Toast
          showToast={showToast}
          setShowToast={setShowToast}
          message={t(error) || error}
        />

        {/* Fixed save bar above the nav */}
        <div style={styles.saveBarWrapper}>
          <button
            type="submit"
            disabled={!canSave}
            style={{
              ...styles.saveBtn,
              opacity: canSave ? 1 : 0.6,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? `${t('save')}…` : t('save')}
          </button>
        </div>
      </form>
    </Base>
  );
};

export default Page;
